import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Bot, User, Radio, CheckCircle2, XCircle, Loader2 } from '../lib/icons';
import { toast } from 'sonner';
import { buildSnapshot, appliquerPropositionReception, updateCuve, fetchCuves } from '../lib/dataProvider';
import { FluxVigie } from '../components/copilote/FluxVigie';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { estAdmin } from '../lib/permissions';
import { fmt, PageTitle } from '../components/shared/badges';
import { estQuestionFournisseur, analyserFournisseur } from '../lib/analyseFournisseur';
import { suggererOfs, texteSuggestionOfs } from '../lib/analytique';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  { id: 'production', label: 'Résumé de la production du jour', prompt: 'Fais-moi un résumé chiffré de la production du jour : tonnages, rendements, filières.' },
  { id: 'cuves', label: 'État critique des cuves', prompt: 'Quelles cuves sont critiques ou proches de la saturation ? Donne le reste disponible et le CMP.' },
  { id: 'fournisseur', label: 'Rechercher un fournisseur', prompt: 'Liste mes fournisseurs avec leur région et le montant total de leurs apports.' },
  { id: 'lampante', label: 'Contrôle filière lampante', prompt: 'Vérifie le cloisonnement de la filière lampante : tonnages, cuves et montants concernés.' },
  { id: 'tresorerie', label: 'Trésorerie & règlements', prompt: 'Où en est ma caisse et quels règlements restent en attente ?' },
  { id: 'flux', label: 'Flux banque / coffre / caisse', prompt: 'Résume les mouvements de fonds entre la banque, le coffre-fort et la caisse Pont-Bascule, et l’écart de rapprochement bancaire.' },
  { id: 'paie', label: 'Pointages & salaires dus', prompt: 'Fais le point sur les pointages saisis et les salaires dus au personnel.' },
  { id: 'saisie', label: 'Créer un bon de réception', prompt: 'Crée un bon de réception fournisseur pour Ferme Ben Salah, 9 500 kg d’olives extra-vierge, achat sur rendement FOSS à 18,2 %.' },
];

const ACCUEIL = {
  role: 'assistant',
  content:
    "Bonjour. Je suis connecté en direct à vos réceptions, cuves, sous-produits et finances. Demandez-moi un résumé, un contrôle de stock, ou dictez-moi un bon de réception à créer.",
};

// Historique persisté par utilisateur : une conversation survit au rafraîchissement,
// à la navigation entre modules et à la fermeture du navigateur.
const cleConversation = (user) => `oliva.copilote.conversation.${user?.email || user?.name || 'poste'}`;

const lireConversation = (user) => {
  if (!user) return null;
  try {
    const brut = JSON.parse(localStorage.getItem(cleConversation(user)) || 'null');
    if (!brut || !Array.isArray(brut.messages) || brut.messages.length === 0) return null;
    return { messages: brut.messages, sessionId: brut.sessionId || `sess-${Date.now()}` };
  } catch {
    return null;
  }
};

const ecrireConversation = (user, messages, sessionId) => {
  if (!user) return;
  try {
    localStorage.setItem(cleConversation(user), JSON.stringify({ messages, sessionId, maj: new Date().toISOString() }));
  } catch {
    // quota dépassé : la conversation reste utilisable en mémoire
  }
};

const Riche = ({ texte }) => (
  <>
    {texte
      .replace(/^#{1,6}\s*/gm, '')
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i} className="font-semibold text-slate-900">
            {part.slice(2, -2)}
          </strong>
        ) : (
          part
        )
      )}
  </>
);

export default function Copilote() {
  const { user } = useAuth();
  const reprise = useRef(lireConversation(user));
  const [messages, setMessages] = useState(reprise.current?.messages ?? [ACCUEIL]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [proposition, setProposition] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const sessionId = useRef(reprise.current?.sessionId ?? `sess-${Date.now()}`);
  const bottom = useRef(null);

  // Toute évolution de la conversation est immédiatement persistée.
  useEffect(() => {
    if (!streaming) ecrireConversation(user, messages, sessionId.current);
  }, [messages, streaming, user]);

  const reinitialiser = () => {
    setMessages([ACCUEIL]);
    setProposition(null);
    sessionId.current = `sess-${Date.now()}`;
    ecrireConversation(user, [ACCUEIL], sessionId.current);
    toast.success('Conversation effacée');
  };

  const loadSnapshot = () => buildSnapshot().then(setSnapshot);
  useEffect(() => {
    loadSnapshot();
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const envoyer = async (texte) => {
    const message = (texte ?? input).trim();
    if (!message || streaming) return;
    setInput('');
    setProposition(null);
    const historique = messages.filter((m) => m !== ACCUEIL);
    setMessages((m) => [...m, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    // Analyse de rentabilité fournisseur / suggestion d'OF : calculées localement sur les données ERP.
    if (/\b(of|ordres? de fabrication|groupage|planifi|prépare|prepare)\b/i.test(message) && /\b(of|ordre|groupage|planifi)/i.test(message)) {
      const filtre = { parcours: /client|prestation/i.test(message) ? 'client' : /fournisseur|achat/i.test(message) ? 'fournisseur' : undefined, date: /aujourd/i.test(message) ? new Date().toISOString().slice(0, 10) : undefined };
      const texte = texteSuggestionOfs(await suggererOfs(filtre));
      setMessages((m) => [...m.slice(0, -1), { role: 'assistant', content: texte }]);
      setStreaming(false);
      return;
    }
    if (estQuestionFournisseur(message)) {
      // Analyse locale uniquement si un fournisseur connu est nommé ; sinon question générale → IA.
      const texte = await analyserFournisseur(message);
      if (texte) {
        setMessages((m) => [...m.slice(0, -1), { role: 'assistant', content: texte }]);
        setStreaming(false);
        return;
      }
    }

    try {
      const snap = await buildSnapshot();
      setSnapshot(snap);
      const res = await fetch(`${API}/copilot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          message,
          history: historique.map((m) => ({ role: m.role, content: m.content })),
          snapshot: snap,
          role: user?.role,
          utilisateur: user?.name,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done = false;

      while (!done) {
        const { value, done: fini } = await reader.read();
        if (fini) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          const ligne = part.trim();
          if (!ligne.startsWith('data:')) continue;
          const ev = JSON.parse(ligne.slice(5).trim());
          if (ev.type === 'delta') {
            setMessages((m) => {
              const copie = [...m];
              copie[copie.length - 1] = {
                role: 'assistant',
                content: copie[copie.length - 1].content + ev.content,
              };
              return copie;
            });
          } else if (ev.type === 'action') {
            setProposition(ev.payload);
          } else if (ev.type === 'error') {
            toast.error('Copilote indisponible', { description: ev.message });
          } else if (ev.type === 'done') {
            done = true;
          }
        }
      }
    } catch (e) {
      toast.error('Erreur de connexion au Copilote', { description: String(e.message ?? e) });
      setMessages((m) => {
        const copie = [...m];
        if (!copie[copie.length - 1].content) {
          copie[copie.length - 1] = {
            role: 'assistant',
            content: 'Connexion interrompue. Réessayez dans un instant.',
          };
        }
        return copie;
      });
    } finally {
      setStreaming(false);
    }
  };

  const confirmer = async () => {
    const { error, row } = await appliquerPropositionReception(proposition);
    if (error) return toast.error(error);
    toast.success(`Réception ${row.id} enregistrée`);
    setProposition(null);
    loadSnapshot();
    setMessages((m) => [
      ...m,
      { role: 'assistant', content: `Réception ${row.id} enregistrée dans le module Réception (statut « En attente »).` },
    ]);
  };

  // Smart Actions déclenchées depuis le flux de la Vigie.
  const navigate = useNavigate();
  const actionVigie = async (action, alerte) => {
    if (action === 'voir_bon') return navigate(`/reception?lot=${alerte.ref}`);
    if (action === 'encaisser') return navigate(`/reception?encaisser=${alerte.ref}`);
    if (action === 'sms_client') {
      const texte = `Huilerie Hassan Ellouze — ${alerte.titre}. ${alerte.message}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(texte)}`, '_blank');
      return toast.info('Message préparé — passerelle SMS Orange non configurée (repli WhatsApp).');
    }
    if (action === 'verrouiller_cuve') {
      await updateCuve(alerte.ref, { verrouillee: true });
      return toast.success('Cuve verrouillée : tout nouveau versement sera refusé.');
    }
    if (action === 'ordre_transfert') {
      const cuves = await fetchCuves();
      const source = cuves.find((c) => c.id === alerte.ref);
      const cible = cuves.find(
        (c) => c.id !== alerte.ref && c.famille === source?.famille && c.capacite - c.volume > 0
      );
      return navigate(
        cible ? `/stocks?transfert=${alerte.ref}&vers=${cible.id}` : '/stocks?transfert=' + alerte.ref
      );
    }
  };

  return (
    <div data-testid="copilote-page" className="space-y-6">      <PageTitle
        emoji="🤖"
        titre="Copilote IA — La Vigie"
        sous="Cockpit de direction proactif : surveillance temps réel, alertes métier et chat analytique (Claude Sonnet 4.6)"
      />

      <div
        data-testid="copilote-live-badge"
        className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-slate-200 border-l-2 border-l-emerald-600 bg-white px-4 py-3"
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-emerald-700">
          <Radio size={14} className="animate-pulse" /> Connecté en temps réel
        </span>
        {snapshot ? (
          <div className="flex flex-wrap gap-x-6 gap-y-1 font-data text-[11px] text-slate-600">
            <span>Réceptions : {fmt(snapshot.receptions.length)}</span>
            <span>Cuves : {fmt(snapshot.cuves.length)}</span>
            <span>Volume nobles : {fmt(snapshot.kpi.volume_cuves_nobles_l)} L</span>
            {estAdmin(user?.role) ? (
              <span>
                Solde caisses : {fmt(snapshot.caisse.comptes.reduce((s, c) => s + c.solde_tnd, 0), 2)} TND
              </span>
            ) : (
              <span data-testid="copilote-soldes-masques">Soldes : hors de votre périmètre</span>
            )}
            <span>Règlements : {fmt(snapshot.reglements.length)}</span>
          </div>
        ) : (
          <span className="font-data text-[11px] text-slate-500">Chargement de l'instantané…</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[420px_1fr]">
        <div className="h-[560px]">
          <FluxVigie onAction={actionVigie} />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        <section className="flex h-[560px] flex-col border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
            <p data-testid="chat-etat-historique" className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Conversation conservée · {messages.filter((m) => m.role === 'user').length} question(s)
            </p>
            <button
              type="button"
              data-testid="chat-effacer"
              onClick={reinitialiser}
              disabled={streaming}
              className="border border-slate-300 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:opacity-40"
            >
              Nouvelle conversation
            </button>
          </div>
          <div data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}
              >
                {m.role === 'assistant' && (
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center bg-slate-900 text-white">
                    <Bot size={14} />
                  </span>
                )}
                <div
                  className={`max-w-[78%] whitespace-pre-wrap px-3 py-2 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-slate-900 text-white'
                      : 'border border-slate-200 bg-slate-50 text-slate-800'
                  }`}
                >
                  {m.content ? (
                    <Riche texte={m.content} />
                  ) : (
                    <span className="flex items-center gap-2 text-slate-500">
                      <Loader2 size={13} className="animate-spin" /> Analyse des données…
                    </span>
                  )}
                </div>
                {m.role === 'user' && (
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center border border-slate-300 text-slate-600">
                    <User size={14} />
                  </span>
                )}
              </div>
            ))}

            {proposition && (
              <div data-testid="proposition-card" className="border border-slate-900 bg-white">
                <p className="border-b border-slate-200 bg-slate-900 px-3 py-2 font-data text-[11px] uppercase tracking-[0.1em] text-white">
                  Action proposée — bon de réception
                </p>
                <dl className="grid grid-cols-2 gap-px bg-slate-200">
                  {[
                    ['Parcours', proposition.parcours],
                    ['Tiers', proposition.tiers],
                    ['Poids brut (Kg)', fmt(proposition.poids_brut)],
                    ['Tare (Kg)', fmt(proposition.tare ?? 0)],
                    ['Qualité', proposition.qualite ?? '—'],
                    ['Mode', proposition.mode ?? '—'],
                    ['FOSS (%)', proposition.foss ? fmt(proposition.foss, 1) : '—'],
                    ['Prix fixe (TND/kg)', proposition.prix_fixe ? fmt(proposition.prix_fixe, 2) : '—'],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white px-3 py-2">
                      <dt className="text-[11px] text-slate-500">{k}</dt>
                      <dd className="font-data text-sm font-semibold text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="flex gap-2 border-t border-slate-200 px-3 py-3">
                  <button
                    type="button"
                    data-testid="proposition-confirmer"
                    onClick={confirmer}
                    className="flex h-10 flex-1 items-center justify-center gap-2 bg-slate-900 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
                  >
                    <CheckCircle2 size={15} /> Confirmer et enregistrer
                  </button>
                  <button
                    type="button"
                    data-testid="proposition-annuler"
                    onClick={() => setProposition(null)}
                    className="flex h-10 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
                  >
                    <XCircle size={15} /> Ignorer
                  </button>
                </div>
              </div>
            )}
            <div ref={bottom} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              envoyer();
            }}
            className="flex gap-2 border-t border-slate-200 px-4 py-3"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez une question ou dictez un ordre de saisie…"
              className="h-11 flex-1 border border-slate-300 bg-slate-50 px-3 text-sm outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={streaming}
              className={`flex h-11 items-center gap-2 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 ${
                streaming ? 'cursor-not-allowed bg-slate-300' : 'bg-slate-900 hover:bg-slate-700'
              }`}
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Envoyer
            </button>
          </form>
        </section>

        <aside data-testid="suggestions-panel" className="border border-slate-200 bg-white">
          <p className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
            <Sparkles size={14} /> Requêtes rapides
          </p>
          <div className="divide-y divide-slate-100">
            {SUGGESTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`suggestion-${s.id}`}
                disabled={streaming}
                onClick={() => envoyer(s.prompt)}
                className="block w-full px-4 py-3 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="border-t border-slate-200 bg-slate-50 px-4 py-3 font-data text-[11px] leading-relaxed text-slate-500">
            Toute création de données passe par une proposition que vous confirmez avant
            enregistrement.
          </p>
        </aside>
      </div>
      </div>
    </div>
  );
}
